import React, { useMemo, useState } from 'react';
import { Modal, Input, Table, Space, Select } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useGetMatClntMaster } from '../../hooks/genericHooks';
import type { MatClntMaster } from '../../types';

export type MatClntMasterMaterialSelectRow = {
  itemcode: string;
  description?: string | null;
  size_text?: string | null;
  unit?: string | null;
};

export interface MatClntMasterMaterialSelectProps {
  open: boolean;
  onOk: (rows: MatClntMasterMaterialSelectRow[]) => void;
  onCancel: () => void;
  title?: string;
}

const norm = (x: any) => (x == null ? '' : String(x)).trim().toLowerCase();

export const MatClntMasterMaterialSelect: React.FC<MatClntMasterMaterialSelectProps> = ({ open, onOk, onCancel, title }) => {
  const { data = [], isLoading } = useGetMatClntMaster() as any;
  const masters: MatClntMaster[] = Array.isArray(data) ? data : (data?.data ?? []);

  const [codeSearch, setCodeSearch] = useState('');
  const [descSearch, setDescSearch] = useState('');
  const [sizeFilter, setSizeFilter] = useState<string>('');
  const [groupFilter, setGroupFilter] = useState<string>('');
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(50);

  // Step 1: apply itemcode and description filters; Step 2: derive size options; Step 3: apply size filter
  const codeDescFiltered = useMemo(() => {
    const code = norm(codeSearch);
    const descVal = norm(descSearch);
    const phraseMatches = Array.from((descVal || '').matchAll(/"([^"]+)"/g)).map(m => m[1].trim()).filter(Boolean);
    const remaining = (descVal || '').replace(/"[^\"]+"/g, ' ').trim();
    const tokens = remaining.split(/\s+/).filter(Boolean);
    return (masters || []).filter((r) => {
      // itemcode starts-with or includes match (lenient)
      const codeOk = code ? String(r.itemcode || '').toLowerCase().includes(code) : true;
      if (!codeOk) return false;
      // description multi-word AND (order independent)
      if (tokens.length === 0 && phraseMatches.length === 0) return true;
      const desc = (r.short_desc || r.long_desc || '') as string;
      const hay = `${desc}`.toLowerCase();
      if (phraseMatches.length > 0 && !phraseMatches.every(p => hay.includes(p))) return false;
      return tokens.every(t => hay.includes(t));
    });
  }, [masters, codeSearch, descSearch]);

  const groupOptions = useMemo(() => {
    const byKey = new Map<string, string>();
    (codeDescFiltered || []).forEach(r => {
      const raw = (r as any)?.material_group as string | undefined;
      if (raw && raw.trim()) { const k = raw.trim(); if (!byKey.has(k)) byKey.set(k, k); }
    });
    return Array.from(byKey.values()).sort().map(v => ({ value: v, label: v }));
  }, [codeDescFiltered]);

  const groupFiltered = useMemo(() => {
    return (codeDescFiltered || []).filter(r => (!groupFilter || String((r as any)?.material_group || '').toLowerCase() === groupFilter.toLowerCase()));
  }, [codeDescFiltered, groupFilter]);

  const sizeOptions = useMemo(() => {
    const byKey = new Map<string, string>();
    (groupFiltered || []).forEach(r => {
      const raw = (r as any)?.size_text as string | undefined;
      if (raw && raw.trim()) { const k = raw.trim(); if (!byKey.has(k)) byKey.set(k, k); }
    });
    return Array.from(byKey.values()).sort().map(v => ({ value: v, label: v }));
  }, [groupFiltered]);

  const filtered = useMemo(() => {
    return (groupFiltered || []).filter(r => (!sizeFilter || String((r as any)?.size_text || '').toLowerCase() === sizeFilter.toLowerCase()));
  }, [groupFiltered, sizeFilter]);

  const dataSource = useMemo(() => (filtered || []).map(r => ({ ...r, key: String(r.itemcode) })), [filtered]);
  const fullMap = useMemo(() => {
    const m = new Map<string, MatClntMaster>();
    (masters || []).forEach(r => m.set(String(r.itemcode), r));
    return m;
  }, [masters]);

  const columns: ColumnsType<any> = [
    { title: 'Item Code', dataIndex: 'itemcode', key: 'itemcode', width: 140 },
    { title: 'Description', key: 'description', ellipsis: true, render: (_: any, r: MatClntMaster) => (r.short_desc || r.long_desc || '') },
    { title: 'Group', dataIndex: 'material_group', key: 'material_group', width: 120 },
    { title: 'Size', dataIndex: 'size_text', key: 'size_text', width: 120 },
    { title: 'Unit', dataIndex: 'unit', key: 'unit', width: 80 },
  ];

  const handleOk = () => {
    const rows = (selectedRowKeys || [])
      .map(k => fullMap.get(String(k)))
      .filter((r): r is MatClntMaster => !!r)
      .map((r) => ({
        itemcode: String(r.itemcode),
        description: (r.short_desc || r.long_desc || null) as string | null,
        size_text: (r as any)?.size_text ?? null,
        unit: (r as any)?.unit ?? null,
      }));
    onOk(rows);
  };

  return (
    <Modal
      open={open}
      title={title || 'Select Client Materials'}
      onOk={handleOk}
      onCancel={onCancel}
      okText="Select"
      width={900}
      zIndex={2000}
    >
      <Space style={{ marginBottom: 8 }} wrap>
        <Input
          placeholder='Item Code'
          allowClear
          value={codeSearch}
          onChange={(e) => setCodeSearch(e.target.value)}
          style={{ width: 160 }}
        />
        <Input
          placeholder='Description (multi-word AND, supports "phrases")'
          allowClear
          value={descSearch}
          onChange={(e) => setDescSearch(e.target.value)}
          style={{ width: 280 }}
        />
        <Select
          placeholder='Group'
          allowClear
          value={groupFilter || undefined}
          onChange={(v) => setGroupFilter(v || '')}
          style={{ width: 160 }}
          showSearch
          optionFilterProp='label'
          filterOption={(input, option) => (String(option?.label || '')).toLowerCase().includes((input || '').toLowerCase())}
          options={groupOptions}
        />
        <Select
          placeholder='Size'
          allowClear
          value={sizeFilter || undefined}
          onChange={(v) => setSizeFilter(v || '')}
          style={{ width: 160 }}
          showSearch
          optionFilterProp='label'
          filterOption={(input, option) => (String(option?.label || '')).toLowerCase().includes((input || '').toLowerCase())}
          options={sizeOptions}
        />
      </Space>
      <Table
        columns={columns}
        dataSource={dataSource}
        rowKey={(r) => r.key}
        loading={isLoading}
        size="small"
        pagination={{ current: page, pageSize, showSizeChanger: true, showTotal: () => `Filtered: ${filtered.length} / Total: ${(masters || []).length}`, onChange: (p, ps) => { setPage(p); setPageSize(ps); } }}
        rowSelection={{ selectedRowKeys, onChange: (keys) => setSelectedRowKeys(keys as React.Key[]), preserveSelectedRowKeys: true, type: 'radio' }}
        onRow={(record) => ({
          onDoubleClick: () => {
            onOk([
              {
                itemcode: String((record as any)?.itemcode),
                description: ((record as any)?.short_desc || (record as any)?.long_desc || null) as string | null,
                size_text: (record as any)?.size_text ?? null,
                unit: (record as any)?.unit ?? null,
              },
            ]);
          },
        })}
        scroll={{ x: 'max-content' }}
      />
    </Modal>
  );
};

export default MatClntMasterMaterialSelect;
